import React, { useState, useEffect, useCallback } from 'react';
import './VocabularyMemory.css';
// Import from organized vocabulary structure
import { vocabularySets, formatCategoryName, VocabPair } from './vocabulary';

// Add the onNavigateHome prop to the interface
interface VocabularyMemoryProps {
  onNavigateHome?: () => void; // Making it optional with ?
}

interface Card {
  id: number;
  content: string;
  type: 'word' | 'definition';
  matched: boolean;
  flipped: boolean;
  pairId: number;
}

// Update the component to accept props
const VocabularyMemory: React.FC<VocabularyMemoryProps> = ({ onNavigateHome }) => {
  // State variables
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<number>(0);
  const [moves, setMoves] = useState<number>(0);
  const [gameOver, setGameOver] = useState<boolean>(false);
  const [difficulty, setDifficulty] = useState<string>('medium');
  const [gradeLevel, setGradeLevel] = useState<string>('elementary');
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  
  // Timer state variables
  const [gameTime, setGameTime] = useState<number>(0); // Time in seconds
  const [isGameActive, setIsGameActive] = useState<boolean>(false);
  const [bestTime, setBestTime] = useState<number | null>(null);

  // Modern card color schemes with gradients
  const cardThemes = [
    { from: 'from-violet-500', to: 'to-purple-600', glow: 'shadow-purple-500/30' },
    { from: 'from-blue-500', to: 'to-cyan-600', glow: 'shadow-cyan-500/30' },
    { from: 'from-emerald-500', to: 'to-green-600', glow: 'shadow-green-500/30' },
    { from: 'from-amber-500', to: 'to-orange-600', glow: 'shadow-orange-500/30' },
    { from: 'from-rose-500', to: 'to-pink-600', glow: 'shadow-pink-500/30' },
    { from: 'from-indigo-500', to: 'to-blue-600', glow: 'shadow-blue-500/30' },
    { from: 'from-teal-500', to: 'to-cyan-600', glow: 'shadow-teal-500/30' },
    { from: 'from-red-500', to: 'to-rose-600', glow: 'shadow-red-500/30' },
  ];

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isGameActive && !gameOver) {
      interval = setInterval(() => {
        setGameTime(prevTime => prevTime + 1);
      }, 1000);
    } else if (interval) {
      clearInterval(interval);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isGameActive, gameOver]);

  // Format time display (MM:SS)
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Memoize the initializeGame function
  const initializeGame = useCallback(() => {
    const selectedVocabulary = vocabularySets[gradeLevel];
    
    if (!selectedVocabulary || selectedVocabulary.length === 0) {
      console.error(`No vocabulary found for grade level: ${gradeLevel}`);
      return;
    }
    
    let pairCount: number;
    if (difficulty === 'easy') {
        pairCount = Math.min(4, selectedVocabulary.length);
    } else if (difficulty === 'medium') {
        pairCount = Math.min(6, selectedVocabulary.length);
    } else {
        pairCount = Math.min(8, selectedVocabulary.length);
    }

    // Ensure we have enough vocabulary pairs
    if (selectedVocabulary.length < pairCount) {
      console.warn(`Not enough vocabulary pairs. Need ${pairCount}, have ${selectedVocabulary.length}`);
      pairCount = selectedVocabulary.length;
    }

    // Get first N pairs and validate them
    const selectedPairs = selectedVocabulary.slice(0, pairCount);
    
    // Debug logging
    console.log(`=== GAME INITIALIZATION ===`);
    console.log(`Grade: ${gradeLevel}, Difficulty: ${difficulty}`);
    console.log(`Selected ${pairCount} pairs:`, selectedPairs);

    const newCards: Card[] = [];
    selectedPairs.forEach((pair, index) => {
        // Validate pair has both word and definition
        if (!pair || !pair.word || !pair.definition) {
          console.error(`Invalid pair at index ${index}:`, pair);
          return; // Skip this pair
        }
        
        // Create word card
        const wordCard: Card = {
            id: index * 2,
            content: String(pair.word).trim(),
            type: 'word',
            matched: false,
            flipped: false,
            pairId: index,
        };
        
        // Create definition card
        const defCard: Card = {
            id: index * 2 + 1,
            content: String(pair.definition).trim(),
            type: 'definition',
            matched: false,
            flipped: false,
            pairId: index,
        };
        
        newCards.push(wordCard);
        newCards.push(defCard);
        
        console.log(`Pair ${index}: "${wordCard.content}" <-> "${defCard.content}"`);
    });

    // Validate all cards have content
    newCards.forEach((card, index) => {
      if (!card.content || card.content.trim() === '') {
        console.error(`BLANK CARD DETECTED at index ${index}:`, card);
      }
    });

    // Better shuffling using Fisher-Yates algorithm
    const shuffled = [...newCards];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    console.log(`Final shuffled cards (${shuffled.length}):`, shuffled.map((c, i) => `${i}: "${c.content}" (${c.type})`));
    console.log(`=== END INITIALIZATION ===`);
    
    // Force a small delay to ensure React state updates properly
    setTimeout(() => {
      setCards(shuffled);
      setFlippedCards([]);
      setMatchedPairs(0);
      setMoves(0);
      setGameOver(false);
      setShowConfetti(false);
      setGameTime(0);
      setIsGameActive(false);
    }, 10);
  }, [difficulty, gradeLevel]);

  // Initialize game on component mount and when settings change
  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  // Check for matches when two cards are flipped
  useEffect(() => {
    if (flippedCards.length === 2) {
      const [firstIndex, secondIndex] = flippedCards;
      const firstCard = cards[firstIndex];
      const secondCard = cards[secondIndex];

      if (firstCard.pairId === secondCard.pairId) {
        // We have a match
        const updatedCards = cards.map(card => {
          if (card.pairId === firstCard.pairId) {
            return { ...card, matched: true };
          }
          return card;
        });
        setCards(updatedCards);
        setMatchedPairs(prevMatched => prevMatched + 1);
        setFlippedCards([]);
      } else {
        // No match, flip back after a delay
        setTimeout(() => {
          const updatedCards = cards.map((card, index) => {
            if (index === firstIndex || index === secondIndex) {
              return { ...card, flipped: false };
            }
            return card;
          });
          setCards(updatedCards);
          setFlippedCards([]);
        }, 1000);
      }
      setMoves(prevMoves => prevMoves + 1);
    }
  }, [flippedCards, cards]);

  // Check if game is over
  useEffect(() => {
    if (matchedPairs === cards.length / 2 && cards.length > 0) {
      setGameOver(true);
      setIsGameActive(false);
      setShowConfetti(true);
      
      // Check if this is a new best time
      if (bestTime === null || gameTime < bestTime) {
        setBestTime(gameTime);
      }
      
      // Hide confetti after 3 seconds
      setTimeout(() => {
        setShowConfetti(false);
      }, 3000);
    }
  }, [matchedPairs, cards, gameTime, bestTime]);

  // Handle card click
  const handleCardClick = (index: number) => {
    // Start the timer on first card click
    if (!isGameActive && !gameOver) {
      setIsGameActive(true);
    }
    
    // Prevent clicking if two cards are already flipped or the card is already matched
    if (flippedCards.length === 2 || cards[index].matched || cards[index].flipped) {
      return;
    }

    // Flip the card
    const updatedCards = cards.map((card, i) => {
      if (i === index) {
        return { ...card, flipped: true };
      }
      return card;
    });
    setCards(updatedCards);
    setFlippedCards([...flippedCards, index]);
  };

  // Reset game
  const resetGame = () => {
    initializeGame();
  };

  // Enhanced confetti component with particles
  const Confetti = () => {
    return (
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-50 overflow-hidden">
        {Array.from({ length: 150 }).map((_, i) => {
          const size = Math.random() * 15 + 8;
          const left = Math.random() * 100;
          const animationDuration = Math.random() * 4 + 3;
          const delay = Math.random() * 2;
          const shapes = ['🎉', '⭐', '🌟', '✨', '🎊', '💫'];
          const shape = shapes[Math.floor(Math.random() * shapes.length)];
          
          return (
            <div
              key={i}
              className="absolute animate-bounce"
              style={{
                left: `${left}%`,
                top: '-20px',
                fontSize: `${size}px`,
                animation: `fall ${animationDuration}s linear forwards`,
                animationDelay: `${delay}s`
              }}
            >
              {shape}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 flex overflow-hidden">
      {showConfetti && <Confetti />}
      

      
      {/* Left Sidebar - Compact Stats & Controls */}
      <div className="w-64 bg-gradient-to-b from-slate-800/95 to-slate-900/95 backdrop-blur-sm border-r border-slate-600/50 flex flex-col">
        
        {/* Stats Section */}
        <div className="flex-1 p-4 space-y-3">
          {/* Best Time */}
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-3 shadow-lg">
            <div className="flex items-center text-white">
              <div className="text-2xl mr-3">🏆</div>
              <div className="flex-1">
                <div className="text-lg font-black font-mono">
                  {bestTime !== null ? formatTime(bestTime) : '--:--'}
                </div>
                <div className="text-xs opacity-80 font-semibold">BEST</div>
              </div>
            </div>
          </div>

          {/* Timer */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-3 shadow-lg">
            <div className="flex items-center text-white">
              <div className="text-2xl mr-3">⏱️</div>
              <div className="flex-1">
                <div className="text-lg font-black font-mono">{formatTime(gameTime)}</div>
                <div className="text-xs opacity-80 font-semibold">TIME</div>
              </div>
            </div>
          </div>

          {/* Moves */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl p-3 shadow-lg">
            <div className="flex items-center text-white">
              <div className="text-2xl mr-3">🎯</div>
              <div className="flex-1">
                <div className="text-lg font-black">{moves}</div>
                <div className="text-xs opacity-80 font-semibold">MOVES</div>
              </div>
            </div>
          </div>

          {/* Pairs */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-3 shadow-lg">
            <div className="flex items-center text-white">
              <div className="text-2xl mr-3">🧩</div>
              <div className="flex-1">
                <div className="text-lg font-black">{matchedPairs}/{cards.length / 2}</div>
                <div className="text-xs opacity-80 font-semibold">PAIRS</div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="p-4 space-y-3 border-t border-slate-600/50">
          {/* Category */}
          <div className="bg-slate-700/50 rounded-2xl p-3">
            <label className="block text-yellow-300 font-bold mb-2 text-sm">📚 CATEGORY</label>
            <select 
              value={gradeLevel} 
              onChange={(e) => setGradeLevel(e.target.value)}
              className="w-full bg-white text-black border border-slate-600 rounded-lg px-3 py-2 text-sm font-medium focus:border-yellow-400 focus:outline-none"
            >
              {Object.keys(vocabularySets).map(category => (
                <option key={category} value={category} className="text-black">
                  {formatCategoryName(category)}
                </option>
              ))}
            </select>
          </div>
          
          {/* Difficulty */}
          <div className="bg-slate-700/50 rounded-2xl p-3">
            <label className="block text-yellow-300 font-bold mb-2 text-sm">⚡ DIFFICULTY</label>
            <select 
              value={difficulty} 
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full bg-white text-black border border-slate-600 rounded-lg px-3 py-2 text-sm font-medium focus:border-yellow-400 focus:outline-none"
            >
              <option value="easy" className="text-black">Easy (4 pairs)</option>
              <option value="medium" className="text-black">Medium (6 pairs)</option>
              <option value="hard" className="text-black">Hard (8 pairs)</option>
            </select>
          </div>
          
          {/* New Game Button */}
          <button 
            onClick={resetGame}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 rounded-2xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg font-bold text-sm"
          >
            🔄 NEW GAME
          </button>

          {/* Home Button */}
          {onNavigateHome && (
            <button 
              onClick={onNavigateHome}
              className="w-full bg-gradient-to-r from-slate-600 to-slate-700 text-white px-4 py-2 rounded-2xl hover:from-slate-700 hover:to-slate-800 transition-all duration-300 shadow-lg font-medium text-sm"
            >
              🏠 HOME
            </button>
          )}
        </div>
      </div>

      {/* Main Game Area */}
      <div className="flex-1 p-8 flex items-center justify-center">
        <div className="w-full max-w-6xl">
          {/* Game Board - Dynamic sizing based on difficulty */}
          <div className={`
            grid gap-4
            ${difficulty === 'easy' ? 'grid-cols-4 max-w-4xl mx-auto' : 
              difficulty === 'medium' ? 'grid-cols-4' : 
              'grid-cols-4'}
          `}>
            {cards.length === 0 ? (
              <div className="col-span-4 text-center text-slate-400 text-xl py-20">
                Loading vocabulary...
              </div>
            ) : (
              cards.map((card, index) => {
                const theme = cardThemes[card.pairId % cardThemes.length];
                
                return (
                  <div
                    key={`card-${card.id}-${index}-${cards.length}`}
                    onClick={() => handleCardClick(index)}
                    className={`
                      relative w-full cursor-pointer
                      transition-all duration-300 transform hover:scale-105
                      ${difficulty === 'easy' ? 'h-40' : 
                        difficulty === 'medium' ? 'h-36' : 
                        'h-32'}
                    `}
                  >
                    {/* Show Card Front (Question Mark) when NOT flipped */}
                    {!card.flipped && !card.matched && (
                      <div className="absolute inset-0 w-full h-full rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 border-4 border-gray-500 shadow-xl flex items-center justify-center">
                        <div className="text-center">
                          <div className={`
                            animate-pulse text-red-600 font-bold
                            ${difficulty === 'easy' ? 'text-7xl' : 
                              difficulty === 'medium' ? 'text-6xl' : 
                              'text-5xl'}
                          `}>❓</div>
                          <div className={`
                            text-gray-600 font-bold mt-1
                            ${difficulty === 'easy' ? 'text-base' : 
                              difficulty === 'medium' ? 'text-sm' : 
                              'text-xs'}
                          `}>FLIP ME</div>
                        </div>
                      </div>
                    )}

                    {/* Show Card Back (Content) when flipped OR matched */}
                    {(card.flipped || card.matched) && (
                      <div className={`
                        absolute inset-0 w-full h-full rounded-2xl
                        ${difficulty === 'easy' ? 'p-5' : 
                          difficulty === 'medium' ? 'p-4' : 
                          'p-3'}
                        ${card.matched 
                          ? 'bg-gradient-to-br from-green-400 to-emerald-600 border-4 border-yellow-400 shadow-green-500/50' 
                          : 'bg-gradient-to-br from-blue-600 to-blue-800 border-4 border-blue-300 shadow-blue-500/30'}
                        shadow-xl flex items-center justify-center text-center
                      `}>
                        <div className="w-full">
                          <div className={`
                            font-black mb-2 px-2 py-1 rounded-full 
                            ${card.type === 'word' ? 'bg-white/20 text-white' : 'bg-white/30 text-white'}
                            ${difficulty === 'easy' ? 'text-sm' : 
                              difficulty === 'medium' ? 'text-xs' : 
                              'text-xs'}
                          `}>
                            {card.type === 'word' ? 'WORD' : 'DEF'}
                          </div>
                          <div className={`
                            font-bold leading-tight break-words hyphens-auto
                            ${card.type === 'word' ? 
                              (difficulty === 'easy' ? 'text-xl' : 
                               difficulty === 'medium' ? 'text-lg' : 
                               'text-base') :
                              (difficulty === 'easy' ? 'text-lg' : 
                               difficulty === 'medium' ? 'text-base' : 
                               'text-sm')
                            }
                          `} style={{ 
                            color: 'white',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.9), 1px 1px 2px rgba(0,0,0,0.8)' 
                          }}>
                            {card.content}
                          </div>
                          {card.matched && (
                            <div className={`
                              mt-2 animate-bounce text-yellow-300
                              ${difficulty === 'easy' ? 'text-3xl' : 
                                difficulty === 'medium' ? 'text-2xl' : 
                                'text-xl'}
                            `} style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>✨</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Game Over Modal */}
      {gameOver && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-40 p-4">
          <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl p-8 shadow-2xl max-w-md w-full text-center border-4 border-yellow-400">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-black text-white mb-4">VICTORY!</h2>
            <div className="space-y-2 text-white mb-6">
              <p className="text-xl">Time: <span className="font-mono font-bold">{formatTime(gameTime)}</span></p>
              <p className="text-xl">Moves: <span className="font-bold">{moves}</span></p>
              {bestTime === gameTime && (
                <p className="text-2xl font-bold text-yellow-300 animate-pulse">🏆 NEW RECORD! 🏆</p>
              )}
            </div>
            <button 
              onClick={resetGame}
              className="bg-white text-purple-600 px-8 py-4 rounded-2xl hover:bg-gray-100 transition-all duration-300 font-bold text-lg shadow-lg transform hover:scale-105"
            >
              PLAY AGAIN
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VocabularyMemory;